import { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import { useApi, appThemeApiRef } from '@backstage/core-plugin-api';
import { useAuth } from '../../auth/AuthContext';
import { authApi } from '../../auth/authApi';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(4),
    maxWidth: 960,
    margin: '0 auto',
  },
  pageTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: '1.4rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(4),
  },
  pageTitleText: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  card: {
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    padding: theme.spacing(3),
  },
  cardTitle: {
    fontSize: '1.05rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(2.5),
    paddingBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  profileRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  avatar: {
    width: 56,
    height: 56,
    borderRadius: '50%',
    background: theme.palette.primary.main,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.5rem',
    fontWeight: 700,
    flexShrink: 0,
  },
  profileName: {
    fontSize: '1.05rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  profileMeta: {
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  editInput: {
    fontSize: '1.05rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    padding: '2px 6px',
    marginBottom: 4,
    width: '100%',
  },
  editButton: {
    marginLeft: 'auto',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    color: theme.palette.primary.main,
    fontWeight: 600,
    fontSize: '0.9rem',
  },
  roleBadge: {
    display: 'inline-block',
    marginTop: 6,
    padding: '2px 10px',
    borderRadius: 12,
    fontSize: '0.75rem',
    fontWeight: 600,
    background: theme.palette.primary.main,
    color: '#fff',
    textTransform: 'capitalize',
  },
  identityRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  identityItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  identityLabel: {
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
    minWidth: 140,
  },
  identityValue: {
    fontSize: '0.9rem',
    color: theme.palette.primary.main,
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  themeGrid: {
    display: 'flex',
    gap: theme.spacing(1.5),
    flexWrap: 'wrap',
    marginTop: theme.spacing(1),
  },
  themeChip: {
    padding: '6px 16px',
    borderRadius: 20,
    border: `2px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: 500,
    background: 'transparent',
    color: theme.palette.text.primary,
    transition: 'all 0.15s',
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
  },
  themeChipActive: {
    borderColor: theme.palette.primary.main,
    background: theme.palette.primary.main,
    color: '#fff',
  },
  themeLabel: {
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  adminActions: {
    display: 'flex',
    alignItems: 'center',
  },
  createUserButton: {
    border: 'none',
    borderRadius: 8,
    padding: '8px 12px',
    color: '#ffffff',
    background: theme.palette.primary.main,
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: '0.85rem',
    lineHeight: 1.2,
    transition: 'all 0.15s ease',
    '&:hover': {
      filter: 'brightness(0.95)',
    },
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.35)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1200,
    padding: theme.spacing(2),
  },
  modalCard: {
    width: '100%',
    maxWidth: 420,
    background: theme.palette.background.paper,
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(3),
  },
  modalTitle: {
    margin: 0,
    marginBottom: theme.spacing(2),
    fontSize: '1.1rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  modalLabel: {
    display: 'block',
    marginBottom: 6,
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
  },
  modalInput: {
    width: '100%',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    padding: '10px 12px',
    marginBottom: theme.spacing(1.5),
    fontSize: '0.9rem',
    outline: 'none',
  },
  modalToggleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  modalToggleInput: {
    width: 16,
    height: 16,
    cursor: 'pointer',
  },
  modalToggleLabel: {
    fontSize: '0.88rem',
    color: theme.palette.text.primary,
    cursor: 'pointer',
  },
  modalError: {
    color: '#bf1f30',
    fontSize: '0.85rem',
    marginTop: 0,
    marginBottom: theme.spacing(1),
  },
  modalSuccess: {
    color: '#0f7a37',
    fontSize: '0.85rem',
    marginTop: 0,
    marginBottom: theme.spacing(1),
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  secondaryButton: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: '8px 12px',
    background: 'transparent',
    color: theme.palette.text.primary,
    cursor: 'pointer',
    fontWeight: 600,
  },
  modalPrimaryButton: {
    border: 'none',
    borderRadius: 8,
    padding: '8px 12px',
    color: '#ffffff',
    background: theme.palette.primary.main,
    cursor: 'pointer',
    fontWeight: 600,
  },
  tableCard: {
    marginTop: theme.spacing(3),
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    padding: theme.spacing(3),
  },
  tableTitle: {
    margin: 0,
    marginBottom: theme.spacing(1.5),
    fontSize: '1.05rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  usersTableWrap: {
    overflowX: 'auto',
  },
  usersTable: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.88rem',
    '& th, & td': {
      textAlign: 'left' as const,
      padding: '10px 8px',
      borderBottom: `1px solid ${theme.palette.divider}`,
      verticalAlign: 'middle' as const,
    },
    '& th': {
      color: theme.palette.text.secondary,
      fontWeight: 700,
      fontSize: '0.8rem',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.03em',
    },
  },
  mutedText: {
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
    margin: 0,
  },
  rolePill: {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: 999,
    fontSize: '0.75rem',
    fontWeight: 600,
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary,
    textTransform: 'capitalize',
  },
}));

export const CustomSettingsPage = () => {
  const classes = useStyles();
  const { user, token, updateProfile } = useAuth();
  const appThemeApi = useApi(appThemeApiRef);

  const [, setActiveThemeId] = useState<string | undefined>(undefined);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);
  const [isCreateUserOpen, setIsCreateUserOpen] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newDisplayName, setNewDisplayName] = useState('');
  const [newIsAdmin, setNewIsAdmin] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [createUserError, setCreateUserError] = useState<string | null>(null);
  const [createUserSuccess, setCreateUserSuccess] = useState<string | null>(null);
  const [managedUsers, setManagedUsers] = useState<Array<{ username: string; displayName: string; roles: string[] }>>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);

  useEffect(() => {
    const subscription = appThemeApi.activeThemeId$().subscribe(id => {
      setActiveThemeId(id ?? undefined);
    });
    return () => subscription.unsubscribe();
  }, [appThemeApi]);

  

  const displayName = user?.displayName ?? user?.username ?? 'Unknown';
  const username = user?.username ?? 'guest';
  const email = `${username}@acme.com`;
  const roles = user?.roles ?? [];
  const initial = displayName.charAt(0).toUpperCase();
  const isAdmin = roles.includes('admin');
  const ownershipLabel = isAdmin ? 'admin' : 'normal user';

  useEffect(() => {
    let mounted = true;
    async function loadUsers() {
      if (!isAdmin || !token) {
        if (mounted) {
          setManagedUsers([]);
          setUsersError(null);
        }
        return;
      }
      setLoadingUsers(true);
      setUsersError(null);
      try {
        const response = await authApi.listUsersByAdmin(token);
        if (mounted) {
          setManagedUsers(response.users);
        }
      } catch (error) {
        if (mounted) {
          setUsersError((error as Error).message || 'Failed to load users');
        }
      } finally {
        if (mounted) {
          setLoadingUsers(false);
        }
      }
    }
    loadUsers();
    return () => {
      mounted = false;
    };
  }, [isAdmin, token]);

  const resetCreateUserForm = () => {
    setNewUsername('');
    setNewPassword('');
    setNewDisplayName('');
    setNewIsAdmin(false);
    setCreateUserError(null);
    setCreateUserSuccess(null);
  };

  const openCreateUserModal = () => {
    resetCreateUserForm();
    setIsCreateUserOpen(true);
  };

  const closeCreateUserModal = () => {
    setIsCreateUserOpen(false);
    resetCreateUserForm();
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setEditName(displayName);
  };

  const handleSaveClick = async () => {
    setSaving(true);
    try {
      if (updateProfile) {
        await updateProfile(editName);
      }
      setIsEditing(false);
    } catch (e) {
      alert(`Failed to save: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateUser = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!token) {
      setCreateUserError('Not authenticated');
      return;
    }

    setCreateUserError(null);
    setCreateUserSuccess(null);
    setCreatingUser(true);

    try {
      const response = await authApi.createUserByAdmin(
        token,
        newUsername.trim(),
        newPassword,
        newIsAdmin,
        newDisplayName.trim() || undefined,
      );
      setManagedUsers(previous => [...previous, response.user]);
      setCreateUserSuccess(
        `User "${response.user.username}" created as ${
          response.user.roles.includes('admin') ? 'admin' : 'normal user'
        }`,
      );
      setNewUsername('');
      setNewPassword('');
      setNewDisplayName('');
      setNewIsAdmin(false);
    } catch (error) {
      setCreateUserError((error as Error).message || 'Failed to create user');
    } finally {
      setCreatingUser(false);
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.pageTitle}>
        <span className={classes.pageTitleText}>⚙️ Settings</span>
        {isAdmin ? (
          <div className={classes.adminActions}>
            <button className={classes.createUserButton} onClick={openCreateUserModal}>
              Create User
            </button>
          </div>
        ) : null}
      </div>

      <div className={classes.grid}>
        {/* Profile Card */}
        <div className={classes.card}>
          <div className={classes.cardTitle} style={{ display: 'flex', alignItems: 'center' }}>
            Profile
            {!isEditing ? (
              <button className={classes.editButton} onClick={handleEditClick}>Edit</button>
            ) : (
              <button className={classes.editButton} onClick={handleSaveClick} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            )}
          </div>
          <div className={classes.profileRow}>
            <div className={classes.avatar}>{initial}</div>
            <div style={{ flex: 1 }}>
              {isEditing ? (
                <input
                  className={classes.editInput}
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  disabled={saving}
                />
              ) : (
                <div className={classes.profileName}>{displayName}</div>
              )}
              <div className={classes.profileMeta}>{email}</div>
              {roles.map(role => (
                <span key={role} className={classes.roleBadge}>{role}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Appearance Card */}
        {/* <div className={classes.card}>
          <div className={classes.cardTitle}>Appearance</div>
          <div className={classes.themeLabel}>Theme</div>
          <div className={classes.themeGrid}>
            {themes.map(t => (
              <button
                key={t.id}
                className={`${classes.themeChip} ${activeThemeId === t.id ? classes.themeChipActive : ''}`}
                onClick={() => appThemeApi.setActiveThemeId(t.id)}
              >
                {t.title}
              </button>
            ))}
            <button
              className={`${classes.themeChip} ${activeThemeId === undefined ? classes.themeChipActive : ''}`}
              onClick={() => appThemeApi.setActiveThemeId(undefined)}
            >
              Auto
            </button>
          </div>
        </div> */}

        {/* Backstage Identity Card */}
        <div className={classes.card}>
          <div className={classes.cardTitle}>Backstage Identity</div>
          <div className={classes.identityRow}>
            <div className={classes.identityItem}>
              <span className={classes.identityLabel}>User Entity</span>
              <span className={classes.identityValue}>
                👤 {username}
              </span>
            </div>
            <div className={classes.identityItem}>
              <span className={classes.identityLabel}>Ownership Entities</span>
              <span className={classes.identityValue}>
                👤 {ownershipLabel}
              </span>
            </div>
          </div>
        </div>
      </div>
      {isCreateUserOpen ? (
        <div className={classes.modalOverlay}>
          <form className={classes.modalCard} onSubmit={handleCreateUser}>
            <h3 className={classes.modalTitle}>Create User</h3>

            <label htmlFor="new-username" className={classes.modalLabel}>
              Username
            </label>
            <input
              id="new-username"
              className={classes.modalInput}
              value={newUsername}
              onChange={e => setNewUsername(e.target.value)}
              autoComplete="username"
              required
              disabled={creatingUser}
            />

            <label htmlFor="new-password" className={classes.modalLabel}>
              Password
            </label>
            <input
              id="new-password"
              type="password"
              className={classes.modalInput}
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              autoComplete="new-password"
              required
              disabled={creatingUser}
            />

            <label htmlFor="new-displayname" className={classes.modalLabel}>
              Display Name (optional)
            </label>
            <input
              id="new-displayname"
              className={classes.modalInput}
              value={newDisplayName}
              onChange={e => setNewDisplayName(e.target.value)}
              autoComplete="name"
              disabled={creatingUser}
            />

            <div className={classes.modalToggleRow}>
              <input
                id="new-is-admin"
                type="checkbox"
                className={classes.modalToggleInput}
                checked={newIsAdmin}
                onChange={e => setNewIsAdmin(e.target.checked)}
                disabled={creatingUser}
              />
              <label htmlFor="new-is-admin" className={classes.modalToggleLabel}>
                Is Admin
              </label>
            </div>

            {createUserError ? <p className={classes.modalError}>{createUserError}</p> : null}
            {createUserSuccess ? <p className={classes.modalSuccess}>{createUserSuccess}</p> : null}

            <div className={classes.modalActions}>
              <button
                type="button"
                className={classes.secondaryButton}
                onClick={closeCreateUserModal}
                disabled={creatingUser}
              >
                Cancel
              </button>
              <button type="submit" className={classes.modalPrimaryButton} disabled={creatingUser}>
                {creatingUser ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </form>
        </div>
      ) : null}
      {isAdmin ? (
        <div className={classes.tableCard}>
          <h3 className={classes.tableTitle}>Created Users</h3>
          {usersError ? <p className={classes.modalError}>{usersError}</p> : null}
          {loadingUsers ? (
            <p className={classes.mutedText}>Loading users...</p>
          ) : managedUsers.filter(managedUser => managedUser.username !== username).length === 0 ? (
            <p className={classes.mutedText}>No users found.</p>
          ) : (
            <div className={classes.usersTableWrap}>
              <table className={classes.usersTable}>
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Display Name</th>
                    <th>Role</th>
                  </tr>
                </thead>
                <tbody>
                  {managedUsers
                    .filter(managedUser => managedUser.username !== username)
                    .map(managedUser => (
                    <tr key={managedUser.username}>
                      <td>{managedUser.username}</td>
                      <td>{managedUser.displayName || managedUser.username}</td>
                      <td>
                        <span className={classes.rolePill}>
                          {managedUser.roles.includes('admin') ? 'admin' : 'normal user'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};
